import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Play,
  Calendar,
  Trophy,
  Zap,
  TrendingUp,
  ArrowUpRight,
  MoreHorizontal,
  Clock,
  Star,
  Hexagon,
  Target,
  Flame,
  BookOpen,
  Video
} from 'lucide-react'

interface Course {
  id: string
  title: string
  instructor: string
  thumbnail: string
  progress: number
  totalLessons: number
  completedLessons: number
  duration: string
  category?: string
}

interface LiveSession {
  id: string
  title: string
  scheduledAt: string
  duration: number
  instructor: string
}

export default function Dashboard() {
  const navigate = useNavigate()
  const [activeCourse, setActiveCourse] = useState<Course | null>(null)
  const [courses, setCourses] = useState<Course[]>([])
  const [upcomingSessions, setUpcomingSessions] = useState<LiveSession[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        navigate('/user/login')
        return
      }

      const [coursesResponse, sessionsResponse] = await Promise.all([
        fetch('/api/enrollments', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/live-sessions?active=true', { headers: { 'Authorization': `Bearer ${token}` } })
      ])

      if (coursesResponse.ok) {
        const data = await coursesResponse.json()
        const enrollments = data.enrollments || []

        const processedCourses = enrollments
          .filter((e: any) => e.course && (e.course.id || e.course._id))
          .map((e: any) => ({
            id: e.course.id || e.course._id,
            title: e.course.title,
            instructor: (e.course.instructor || 'Sudha\'s Academy').replace(/Sudha Sharma/i, 'Sudha'),
            thumbnail: e.course.thumbnail || e.course.image || '/api/placeholder/400/225',
            progress: e.progress || 0,
            totalLessons: e.course.totalLessons || 10,
            completedLessons: Math.round((e.progress || 0) * (e.course.totalLessons || 10) / 100),
            duration: e.course.duration || '4 weeks',
            category: e.course.category?.name || 'Beauty'
          }))

        // Set the most recently accessed or highest progress course as active
        if (processedCourses.length > 0) {
          setActiveCourse(processedCourses[0])
          setCourses(processedCourses.slice(1)) // Remaining courses
        }
      }

      if (sessionsResponse.ok) {
        const data = await sessionsResponse.json()
        setUpcomingSessions((data.sessions || []).slice(0, 2))
      }
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  const user = JSON.parse(localStorage.getItem('user') || '{}')
  const firstName = user.name?.split(' ')[0] || 'Creator'

  if (loading) return <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center"><div className="w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full animate-spin"></div></div>

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0F19] p-4 md:p-8 font-sans text-slate-900 dark:text-slate-100">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Header with Date & Greeting */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-2">
          <div>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
            <h1 className="text-3xl md:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-slate-600 dark:from-white dark:to-slate-400">
              Hello, {firstName}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-900 rounded-full border border-slate-200 dark:border-slate-800 shadow-sm">
              <Flame className="w-4 h-4 text-orange-500 fill-orange-500" />
              <span className="font-bold text-sm">3 Day Streak</span>
            </div>
            <button className="p-2 bg-white dark:bg-slate-900 rounded-full border border-slate-200 dark:border-slate-800 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
              <Target className="w-5 h-5 text-slate-600 dark:text-slate-400" />
            </button>
          </div>
        </div>

        {/* BENTO GRID LAYOUT */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 auto-rows-[minmax(180px,auto)]">

          {/* 1. HERO: Active Course (Span 2x2) */}
          <div className="md:col-span-2 md:row-span-2 relative group overflow-hidden rounded-3xl bg-slate-900 text-white shadow-2xl shadow-violet-900/20">
            {activeCourse ? (
              <>
                <div className="absolute inset-0">
                  <img
                    src={activeCourse.thumbnail}
                    alt="Active Course"
                    className="w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent" />
                </div>

                <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-between">
                  <div className="flex justify-between items-start">
                    <span className="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-medium border border-white/10">
                      In Progress
                    </span>
                    <button className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center hover:bg-white/20 transition-colors">
                      <MoreHorizontal className="w-5 h-5" />
                    </button>
                  </div>

                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold mb-2 leading-tight">{activeCourse.title}</h2>
                    <p className="text-slate-300 mb-6 line-clamp-1">{activeCourse.instructor}</p>

                    <div className="space-y-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-300">{activeCourse.progress}% Complete</span>
                        <span className="text-slate-300">{activeCourse.completedLessons}/{activeCourse.totalLessons} Lessons</span>
                      </div>
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500"
                          style={{ width: `${activeCourse.progress}%` }}
                        />
                      </div>

                      <button
                        onClick={() => navigate(`/user/courses/learn/${activeCourse.id}`)}
                        className="w-full py-4 bg-white text-slate-900 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-slate-100 transition-colors shadow-lg shadow-white/10"
                      >
                        <Play className="w-5 h-5 fill-current" />
                        Continue Learning
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center">
                <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mb-4 backdrop-blur-md">
                  <BookOpen className="w-8 h-8 text-violet-400" />
                </div>
                <h3 className="text-xl font-bold mb-2">No Active Course</h3>
                <p className="text-slate-400 mb-6">Start your journey by enrolling in a course.</p>
                <button
                  onClick={() => navigate('/user/courses')}
                  className="px-6 py-3 bg-violet-600 hover:bg-violet-500 text-white rounded-xl font-semibold transition-colors"
                >
                  Browse Courses
                </button>
              </div>
            )}
          </div>

          {/* 2. STATS: Skill Radar (Span 1x1) */}
          <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col justify-between relative overflow-hidden">
            <div className="absolute top-0 right-0 p-3 opacity-10">
              <Hexagon className="w-24 h-24 text-violet-500" />
            </div>
            <div>
              <h3 className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Zap className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                Skill Level
              </h3>
              <p className="text-xs text-slate-500 mt-1">Based on completed modules</p>
            </div>

            <div className="flex-1 flex items-center justify-center py-4">
              {/* SVG Radar/Gauge Chart */}
              <div className="relative w-28 h-28 flex items-center justify-center">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="56"
                    cy="56"
                    r="48"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="transparent"
                    className="text-slate-100 dark:text-slate-800"
                  />
                  <circle
                    cx="56"
                    cy="56"
                    r="48"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="transparent"
                    strokeDasharray={2 * Math.PI * 48}
                    strokeDashoffset={2 * Math.PI * 48 * (1 - 0.65)}
                    className="text-violet-500 drop-shadow-[0_0_10px_rgba(139,92,246,0.5)]"
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-black text-slate-900 dark:text-white">
                    14
                  </span>
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Level</span>
                </div>
              </div>
            </div>
            <div className="text-center">
              <span className="text-xs font-bold px-2 py-1 bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-300 rounded-md">
                Novice Artist
              </span>
            </div>
          </div>

          {/* 3. SCHEDULE: Upcoming Live (Span 1x2) */}
          <div className="md:row-span-2 bg-slate-900 text-white rounded-3xl p-6 flex flex-col relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-fuchsia-500/20 blur-3xl rounded-full -mr-10 -mt-10"></div>

            <div className="flex items-center justify-between mb-6 relative z-10">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-fuchsia-400" />
                Schedule
              </h3>
              <button onClick={() => navigate('/user/live-sessions')} className="text-xs text-fuchsia-300 hover:text-white transition-colors">View All</button>
            </div>

            <div className="flex-1 space-y-4 relative z-10 overflow-y-auto pr-1 custom-scrollbar">
              {upcomingSessions.length > 0 ? (
                upcomingSessions.map((session, idx) => (
                  <div key={session.id} className="group relative pl-4 pb-4 border-l-2 border-slate-700 last:border-0">
                    <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-slate-900 border-2 border-fuchsia-500 group-hover:bg-fuchsia-500 transition-colors"></div>
                    <div className="bg-white/5 p-4 rounded-2xl hover:bg-white/10 transition-colors border border-white/5">
                      <p className="text-xs text-fuchsia-300 font-medium mb-1">
                        {new Date(session.scheduledAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} • {new Date(session.scheduledAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                      <h4 className="font-bold text-sm mb-2 line-clamp-2">{session.title}</h4>
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        <Clock className="w-3 h-3" />
                        <span>{session.duration} min</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center opacity-60">
                  <Calendar className="w-12 h-12 mb-3 text-slate-600" />
                  <p className="text-sm">No upcoming sessions</p>
                </div>
              )}
            </div>

            <button className="mt-4 w-full py-3 bg-fuchsia-600 hover:bg-fuchsia-500 rounded-xl font-semibold text-sm transition-colors shadow-lg shadow-fuchsia-900/20">
              Join Next Session
            </button>
          </div>

          {/* 4. QUICK ACTION: Certificates (Span 1x1) */}
          <div
            onClick={() => navigate('/user/certificates')}
            className="group bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-3xl p-6 border border-emerald-100 dark:border-emerald-900/30 cursor-pointer hover:shadow-md transition-all"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="w-10 h-10 bg-white dark:bg-emerald-900/50 rounded-full flex items-center justify-center shadow-sm text-emerald-600 dark:text-emerald-400">
                <Trophy className="w-5 h-5" />
              </div>
              <div className="w-8 h-8 rounded-full bg-emerald-200/50 dark:bg-emerald-800/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <ArrowUpRight className="w-4 h-4 text-emerald-700 dark:text-emerald-300" />
              </div>
            </div>
            <h3 className="font-bold text-slate-900 dark:text-white text-lg">Certificates</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">View your achievements</p>
          </div>

          {/* 5. LIST: Other Courses (Span 2x1) */}
          <div className="md:col-span-2 bg-white dark:bg-slate-900 rounded-3xl p-6 border border-slate-200 dark:border-slate-800 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-slate-400" />
                Your Library
              </h3>
              <button onClick={() => navigate('/user/courses')} className="text-sm font-medium text-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors">
                View All
              </button>
            </div>

            <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
              {courses.length > 0 ? (
                courses.map(course => (
                  <div
                    key={course.id}
                    onClick={() => navigate(`/user/courses/learn/${course.id}`)}
                    className="flex-shrink-0 w-64 p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer border border-transparent hover:border-slate-200 dark:hover:border-slate-700"
                  >
                    <div className="flex items-center gap-3">
                      <img src={course.thumbnail} alt="" className="w-12 h-12 rounded-lg object-cover" />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-sm truncate text-slate-900 dark:text-white">{course.title}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                            <div className="h-full bg-slate-900 dark:bg-slate-400 rounded-full" style={{ width: `${course.progress}%` }}></div>
                          </div>
                          <span className="text-xs text-slate-500">{course.progress}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="w-full py-8 text-center text-slate-400 text-sm bg-slate-50 dark:bg-slate-800/30 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700">
                  No other courses in your library
                </div>
              )}
            </div>
          </div>

          {/* 6. WIDGET: Community/Trending (Span 1x1) */}
          <div className="bg-gradient-to-br from-indigo-500 to-violet-600 rounded-3xl p-6 text-white flex flex-col justify-between shadow-lg shadow-indigo-500/20">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-indigo-200" />
              <span className="font-bold text-indigo-100 text-sm">Trending Now</span>
            </div>
            <div>
              <h3 className="font-bold text-xl leading-tight mb-1">Mastering Bridal Makeup</h3>
              <p className="text-indigo-100 text-xs mb-3">Most popular this week</p>
              <button
                onClick={() => navigate('/user/courses')}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-lg text-xs font-bold transition-colors"
              >
                Check it out
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}